import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  COMPANY,
  computeInvoiceTotals,
  formatDisplayDate,
  formatMoney,
  lineAmount,
} from '../utils/invoiceModelDefaults';
import { INVOICE_THEME } from '../utils/invoiceDesignTokens';

export default function InvoiceTemplatePreview({ model }) {
  const { subtotal, taxAmount, total, gstPercent } = computeInvoiceTotals(model);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: `${INVOICE_THEME.radiusLg}px`,
        overflow: 'hidden',
        boxShadow: INVOICE_THEME.shadow,
        border: `1px solid ${INVOICE_THEME.border}`,
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        color: INVOICE_THEME.ink,
        position: 'relative',
      }}
    >
      <Box
        sx={{
          height: 5,
          background: INVOICE_THEME.gradient,
        }}
      />

      <Box sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 3,
            mb: 4,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '14px',
                bgcolor: '#f8fafc',
                border: `1px solid ${INVOICE_THEME.border}`,
              }}
            >
              <Box
                component="img"
                src={COMPANY.logoSrc}
                alt={COMPANY.name}
                sx={{ height: 48, width: 'auto', display: 'block' }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: '1.35rem',
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  background: INVOICE_THEME.gradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {COMPANY.name}
              </Typography>
              <Typography sx={{ fontSize: '0.75rem', color: INVOICE_THEME.slate, mt: 0.25 }}>
                Intelligent disinfection solutions
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography
              sx={{
                fontSize: 'clamp(1.4rem, 2.5vw, 1.85rem)',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: INVOICE_THEME.ink,
                lineHeight: 1.1,
              }}
            >
              Invoice {model.invoiceNumber || '—'}
            </Typography>
            <Box
              sx={{
                display: 'inline-block',
                mt: 1,
                px: 1.25,
                py: 0.35,
                borderRadius: '999px',
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: '#7c3aed',
                bgcolor: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.2)',
              }}
            >
              TAX INVOICE
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '14px',
              bgcolor: '#f8fafc',
              border: `1px solid ${INVOICE_THEME.border}`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.68rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                color: INVOICE_THEME.slate,
                mb: 1,
              }}
            >
              BILL TO
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', mb: 0.5 }}>
              {model.billToName || 'Your Client'}
            </Typography>
            <Typography
              sx={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.65, whiteSpace: 'pre-line' }}
            >
              {model.billToAddress || 'Client address line 1\nCity, State, PIN'}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: 1,
              alignContent: 'start',
              fontSize: '0.84rem',
              color: '#475569',
            }}
          >
            {[
              ['Issue date', formatDisplayDate(model.issueDate)],
              ['Due date', formatDisplayDate(model.dueDate)],
              ['Reference', model.reference || model.invoiceNumber || '—'],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  py: 0.75,
                  px: 1.5,
                  borderRadius: '10px',
                  bgcolor: '#fafbfc',
                  border: `1px solid ${INVOICE_THEME.border}`,
                }}
              >
                <span style={{ color: INVOICE_THEME.slate, fontWeight: 600 }}>{label}</span>
                <span style={{ fontWeight: 600, color: INVOICE_THEME.ink }}>{value}</span>
              </Box>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            borderRadius: '14px',
            overflow: 'hidden',
            mb: 3.5,
            boxShadow: '0 4px 14px rgba(232, 168, 56, 0.25)',
          }}
        >
          {[
            ['Invoice No.', model.invoiceNumber || '—'],
            ['Issue date', formatDisplayDate(model.issueDate)],
            ['Due date', formatDisplayDate(model.dueDate)],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                background: `linear-gradient(135deg, ${INVOICE_THEME.gold} 0%, ${INVOICE_THEME.goldDark} 100%)`,
                color: '#fff',
                px: 2,
                py: 1.75,
                fontSize: '0.78rem',
                fontWeight: 700,
                lineHeight: 1.55,
              }}
            >
              {label}
              <Box component="span" sx={{ display: 'block', fontSize: '0.92rem', mt: 0.25 }}>
                {value}
              </Box>
            </Box>
          ))}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              color: '#fff',
              px: 2,
              py: 1.75,
              fontSize: '0.78rem',
              fontWeight: 700,
              lineHeight: 1.55,
              textAlign: 'right',
            }}
          >
            Total due ({model.currency || 'INR'})
            <Box component="span" sx={{ display: 'block', fontSize: '1rem', mt: 0.25 }}>
              {formatMoney(total, model.currency)}
            </Box>
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto', borderRadius: '14px', border: `1px solid ${INVOICE_THEME.border}` }}>
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
            <Box
              component="thead"
              sx={{ bgcolor: '#f1f5f9' }}
            >
              <Box component="tr">
                {['Description', 'Qty', 'Unit price', 'Amount'].map((head, i) => (
                  <Box
                    component="th"
                    key={head}
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: INVOICE_THEME.slate,
                      textAlign: i === 0 ? 'left' : i === 1 ? 'center' : 'right',
                      px: 2,
                      py: 1.25,
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {head}
                    {i > 1 ? ` (${model.currency === 'INR' ? '₹' : model.currency})` : ''}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {(model.lineItems || []).map((item, index) => (
                <Box
                  component="tr"
                  key={item.id}
                  sx={{
                    bgcolor: index % 2 === 0 ? '#fff' : '#fafbfc',
                    '&:last-child td': { borderBottom: 'none' },
                  }}
                >
                  <Box
                    component="td"
                    sx={{ px: 2, py: 1.35, fontSize: '0.88rem', borderBottom: `1px solid ${INVOICE_THEME.border}` }}
                  >
                    {item.description || '—'}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 2,
                      py: 1.35,
                      fontSize: '0.88rem',
                      textAlign: 'center',
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {item.quantity === '' || item.quantity == null ? '—' : item.quantity}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 2,
                      py: 1.35,
                      fontSize: '0.88rem',
                      textAlign: 'right',
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {formatMoney(item.unitPrice, model.currency)}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 2,
                      py: 1.35,
                      fontSize: '0.88rem',
                      fontWeight: 600,
                      textAlign: 'right',
                      borderBottom: `1px solid ${INVOICE_THEME.border}`,
                    }}
                  >
                    {formatMoney(lineAmount(item), model.currency)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Box
            sx={{
              minWidth: 280,
              p: 2,
              borderRadius: '14px',
              bgcolor: '#f8fafc',
              border: `1px solid ${INVOICE_THEME.border}`,
            }}
          >
            {[
              ['Subtotal', formatMoney(subtotal, model.currency)],
              [`GST ${gstPercent}%`, formatMoney(taxAmount, model.currency)],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.88rem', color: '#475569' }}
              >
                <span>{label}</span>
                <span>{value}</span>
              </Box>
            ))}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 1.25,
                mt: 1,
                borderTop: `2px solid ${INVOICE_THEME.border}`,
                fontWeight: 800,
                fontSize: '1.05rem',
                color: INVOICE_THEME.ink,
              }}
            >
              <span>Total ({model.currency || 'INR'})</span>
              <span style={{ background: INVOICE_THEME.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                {formatMoney(total, model.currency)}
              </span>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontSize: '0.78rem', color: INVOICE_THEME.slate, fontWeight: 600 }}>
            Issuer&apos;s signature
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontFamily: '"Segoe Script", "Brush Script MT", cursive',
              fontSize: '1.6rem',
              color: '#334155',
              minHeight: 36,
            }}
          >
            {model.issuerSignature || '\u00A0'}
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 4,
            pt: 2,
            borderTop: `1px solid ${INVOICE_THEME.border}`,
            textAlign: 'center',
            fontSize: '0.78rem',
            color: INVOICE_THEME.slate,
            lineHeight: 1.9,
          }}
        >
          {model.companyPhone} &nbsp;·&nbsp; {model.companyWebsite} &nbsp;·&nbsp; {model.companyEmail}
          <br />
          {model.companyAddress}
        </Box>
      </Box>
    </Box>
  );
}
